/**
 * Design Studio element metadata — label, marker code, and tint per marking
 * type (PRD R3). Colour is grouping only; the marker also carries a code
 * letter so state is never colour-alone.
 */
export const ELEMENT_TYPES = [
  "logo_print",
  "embroidery_stone",
  "button",
  "pocket",
  "placket",
  "collar",
  "cuff",
  "stitch",
  "label",
  "wash_care",
  "text",
  "trim",
  "packing",
] as const;
export type StudioElementType = (typeof ELEMENT_TYPES)[number];

export interface ElementMeta {
  label: string;
  /** Two-letter marker code shown inside the canvas dot. */
  code: string;
  /** marker chip classes (bg + text). */
  chip: string;
}

export const ELEMENT_META: Record<StudioElementType, ElementMeta> = {
  logo_print: { label: "Logo / print", code: "LP", chip: "bg-sky-600 text-white" },
  embroidery_stone: { label: "Embroidery / stone", code: "ES", chip: "bg-sky-100 text-sky-700" },
  button: { label: "Button", code: "BT", chip: "bg-warn-100 text-warn" },
  pocket: { label: "Pocket", code: "PK", chip: "bg-ok-100 text-ok" },
  placket: { label: "Placket", code: "PL", chip: "bg-warn-100 text-warn" },
  collar: { label: "Collar", code: "CO", chip: "bg-line text-ink-2" },
  cuff: { label: "Cuff", code: "CF", chip: "bg-line text-ink-2" },
  stitch: { label: "Stitch", code: "ST", chip: "bg-line text-ink-2" },
  label: { label: "Label", code: "LB", chip: "bg-bad-100 text-bad" },
  wash_care: { label: "Wash care", code: "WC", chip: "bg-bad-100 text-bad" },
  text: { label: "Text", code: "TX", chip: "bg-line text-ink-2" },
  trim: { label: "Trim", code: "TR", chip: "bg-ok-100 text-ok" },
  packing: { label: "Packing", code: "PG", chip: "bg-line text-ink-2" },
};

export function elementLabel(type: string): string {
  return ELEMENT_META[type as StudioElementType]?.label ?? type;
}

/** mm → display cm string (97 → "9.7"). */
export function mmToCmText(mm: number | null): string {
  if (mm === null) return "";
  return (mm / 10).toFixed(mm % 10 === 0 ? 0 : 1);
}

/** display cm string → mm int ("9.7" → 97); null when blank/invalid. */
export function cmTextToMm(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const value = Number.parseFloat(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 10);
}
